import { FC, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY,
  FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY,
} from 'common/constants/services/firms';
import { useStore } from 'common/store';
import {
  ColType,
  DEFAULT_FIRM_SUMMARY_COLUMNS,
  getValueTypesForSummary,
  normalizeFirmSpreadsheetDefinition,
  SortKey,
} from 'common/types/summaryDefinitions';
import { OrderedDefinition, useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { FirmPendingDecisions, NoCompanyMessage, NoInformationMessage } from 'pages/Firms/components';
import { useFirmSummaryTab } from 'pages/Firms/hooks';
import { isAddableFirmSummaryColumnType } from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import AddColumnsModal from 'pages/Firms/pages/FirmSummary/AddColumnsModalChecklist';
import { getObjectValue } from 'utilities';
import { isProcessManagementColumnEnabled } from './useDashboardFieldAvailability';

interface FirmSummaryBodyProps {
  // Non-null guarantee: the parent only mounts the body once a definition is active,
  // so useFirmSummaryTab never runs with definitionId=undefined (which would trigger
  // the legacy useGetFirmSummary path and cause a skeleton/content flicker on first load).
  activeDefinition: OrderedDefinition;
  summaryType?: 'firm' | 'valuations';
}

const canEditDefinition = (definition: OrderedDefinition): boolean => {
  if (definition.isGlobal) {
    return Boolean(definition.firm || definition.fund);
  }
  return true;
};

const FirmSummaryBody: FC<FirmSummaryBodyProps> = ({ activeDefinition, summaryType }) => {
  const queryClient = useQueryClient();
  const { updateDefinition, refetch, isSaving } = useSummaryDefinitions();

  const definitionDataQueryKey =
    summaryType === 'valuations' ? FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY : FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY;

  const [isAddColumnsModalOpen, setIsAddColumnsModalOpen] = useState(false);

  const availableValueTypes = useMemo(() => getValueTypesForSummary('firm'), []);
  const valueTypeById = useMemo(
    () => new Map(availableValueTypes.map(valueType => [valueType.id, valueType])),
    [availableValueTypes]
  );

  const [storeValue] = useStore();
  const currentFirm = getObjectValue(getObjectValue(storeValue).firmInfo);
  // Shared with FirmSummaryWorkspace's column editor and the dashboard edit dialog: the firm flag
  // gates Process Management availability. Without it, the spreadsheet Add Columns modal would let
  // users select and save a column the picker hides and rendering immediately strips.
  const isProcessManagementEnabled = isProcessManagementColumnEnabled({
    currentFirm,
    summaryType,
  });

  const addableColumns = useMemo(
    () => availableValueTypes.filter(vt => isAddableFirmSummaryColumnType(vt.id, isProcessManagementEnabled)),
    [availableValueTypes, isProcessManagementEnabled]
  );

  const spreadsheetDefinition = activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition;

  const handleSortToggle = useCallback(
    async (columnType: ColType) => {
      if (!canEditDefinition(activeDefinition)) {
        return;
      }

      const currentDef = normalizeFirmSpreadsheetDefinition(spreadsheetDefinition);
      const currentSortKeys = currentDef.sortKeys ?? [];
      const existingIndex = currentSortKeys.findIndex(([col]) => col === columnType);

      let nextSortKeys: SortKey[];
      if (existingIndex === -1) {
        nextSortKeys = [...currentSortKeys, [columnType, 'asc']];
      } else if (currentSortKeys[existingIndex][1] === 'asc') {
        nextSortKeys = currentSortKeys.map((sk, idx) => (idx === existingIndex ? ([sk[0], 'desc'] as SortKey) : sk));
      } else {
        nextSortKeys = currentSortKeys.filter((_, idx) => idx !== existingIndex);
      }

      await updateDefinition(activeDefinition.id, {
        spreadsheet_definition: { ...currentDef, sortKeys: nextSortKeys },
      });
      await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);
    },
    [activeDefinition, definitionDataQueryKey, queryClient, refetch, spreadsheetDefinition, updateDefinition]
  );

  const handleClearFilters = useCallback(async () => {
    const currentDef = normalizeFirmSpreadsheetDefinition(spreadsheetDefinition);
    await updateDefinition(activeDefinition.id, {
      spreadsheet_definition: { ...currentDef, filters: [] },
    });
    await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);
  }, [activeDefinition.id, definitionDataQueryKey, queryClient, refetch, spreadsheetDefinition, updateDefinition]);

  const handleClearSingleFilter = useCallback(
    async (index: number) => {
      const currentDef = normalizeFirmSpreadsheetDefinition(spreadsheetDefinition);
      const updatedFilters = (currentDef.filters ?? []).filter((_, idx) => idx !== index);
      await updateDefinition(activeDefinition.id, {
        spreadsheet_definition: { ...currentDef, filters: updatedFilters },
      });
      await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);
    },
    [activeDefinition.id, definitionDataQueryKey, queryClient, refetch, spreadsheetDefinition, updateDefinition]
  );

  const handleClearSorts = useCallback(async () => {
    const currentDef = normalizeFirmSpreadsheetDefinition(spreadsheetDefinition);
    await updateDefinition(activeDefinition.id, {
      spreadsheet_definition: { ...currentDef, sortKeys: [] },
    });
    await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);
  }, [activeDefinition.id, definitionDataQueryKey, queryClient, refetch, spreadsheetDefinition, updateDefinition]);

  const handleClearSingleSort = useCallback(
    async (columnType: ColType) => {
      const currentDef = normalizeFirmSpreadsheetDefinition(spreadsheetDefinition);
      const updatedSortKeys = (currentDef.sortKeys ?? []).filter(([col]) => col !== columnType);
      await updateDefinition(activeDefinition.id, {
        spreadsheet_definition: { ...currentDef, sortKeys: updatedSortKeys },
      });
      await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);
    },
    [activeDefinition.id, definitionDataQueryKey, queryClient, refetch, spreadsheetDefinition, updateDefinition]
  );

  const handleOpenAddColumnsModal = useCallback(() => setIsAddColumnsModalOpen(true), []);
  const handleCloseAddColumnsModal = useCallback(() => setIsAddColumnsModalOpen(false), []);

  const handleAddColumnsFromModal = useCallback(
    async (newColumns: ColType[]) => {
      const currentDef = normalizeFirmSpreadsheetDefinition(spreadsheetDefinition);
      const updatedDef = {
        ...currentDef,
        columns: Object.fromEntries(newColumns.map((col, idx) => [idx, col])),
      };

      await updateDefinition(activeDefinition.id, {
        spreadsheet_definition: updatedDef,
      });

      await Promise.all([refetch(), queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] })]);
    },
    [activeDefinition.id, definitionDataQueryKey, queryClient, refetch, spreadsheetDefinition, updateDefinition]
  );

  const { firmSummaryTab, hasNoCompanies, hasNoInvestments, isFilteredEmpty } = useFirmSummaryTab({
    spreadsheetDefinition,
    definitionId: activeDefinition.id,
    summaryType,
    onSortToggle: handleSortToggle,
    valueTypeById,
    onClearFilters: handleClearFilters,
    onRemoveFilter: handleClearSingleFilter,
    onClearSorts: handleClearSorts,
    onRemoveSort: handleClearSingleSort,
    onAddColumn: handleOpenAddColumnsModal,
  });

  return (
    <>
      <FirmPendingDecisions />

      {hasNoCompanies && <NoCompanyMessage />}
      {!hasNoCompanies && hasNoInvestments && !isFilteredEmpty && <NoInformationMessage />}

      {firmSummaryTab.content}

      <AddColumnsModal
        open={isAddColumnsModalOpen}
        onClose={handleCloseAddColumnsModal}
        onSave={handleAddColumnsFromModal}
        availableColumns={addableColumns}
        currentColumns={
          spreadsheetDefinition?.columns
            ? (Object.values(spreadsheetDefinition.columns) as ColType[])
            : (DEFAULT_FIRM_SUMMARY_COLUMNS as ColType[])
        }
        groupBy={spreadsheetDefinition?.groupBy}
        disabled={isSaving}
      />
    </>
  );
};

export default FirmSummaryBody;
